import { Icon } from '@eventbrite/eds-icon';
import { gettext } from '@eventbrite/i18n';
import React from 'react';
import { ONLINE_LOCATION_SLUG, SOCIAL_TYPE_ICON_MAP } from '../../constants';
import { useLocationContext } from '../../context';
import './HeroSection.scss';
import { PlaceDescriptionData } from './PlaceDescriptionData';
export interface HeroSectionProps {
    placeImage?: string;
    isPageArt: boolean;
    artistName?: string;
    artistInstagramLink?: string;
}
interface PlaceImageProps {
    shortPlaceName?: string;
    placeImage?: string;
    height: number;
    width: number;
}

const PlaceImage = ({
    shortPlaceName,
    placeImage,
    height,
    width,
}: PlaceImageProps) => {
    let component = null;
    const altText = gettext('Things to do in %(shortPlaceName)s', {
        shortPlaceName: shortPlaceName,
    }).toString();
    if (placeImage) {
        component = (
            <img
                //eslint-disable-next-line
                //@ts-ignore
                fetchpriority="high"
                src={placeImage}
                alt={altText}
                loading="eager"
                height={height}
                width={width}
            />
        );
    }
    return component;
};

interface PageArtProps {
    className: string;
    artistName?: string;
    artistInstagramLink?: string;
}
const PageArt = ({
    className,
    artistName,
    artistInstagramLink,
}: PageArtProps) => {
    const pageArtCourtesy = gettext('Page art courtesy of ');
    const iconType = SOCIAL_TYPE_ICON_MAP['instagram'] || '';
    return (
        <div className={className}>
            <span className="page-art-courtesy">{pageArtCourtesy}</span>
            {'  '}
            <a
                href={artistInstagramLink}
                className="page-art-link"
                rel="noopener noreferrer"
                target="_blank"
            >
                {artistName}
                <Icon color="white" type={iconType} />
            </a>
        </div>
    );
};

export const getGeoAndPlaceDescription = (
    shortPlaceName?: string,
    locationSlug?: string,
) => {
    let geo = gettext('IN %(geo)s', {
        geo: shortPlaceName?.toUpperCase(),
    });

    let placeDescription = gettext(
        "Welcome to %(geo)s! Check out the latest events in town. Explore by location, trending searches, top organizers, and more. Whether you're a local, new to the area, or cruising through, there's no shortage of unique things to do.",
        {
            geo: shortPlaceName,
        },
    );

    if (locationSlug && PlaceDescriptionData[locationSlug]) {
        placeDescription = gettext('%(description)s', {
            description: PlaceDescriptionData[locationSlug],
        });
    }

    if (locationSlug == ONLINE_LOCATION_SLUG) {
        geo = gettext('ONLINE');
        placeDescription = gettext(
            'Discover a world of endless possibilities with online events. Enjoy virtual tours, talks, cooking classes, and more. Dive into the excitement of a live experience wherever you are.',
        );
    }

    return { geo, placeDescription };
};

export const HeroSection: React.FunctionComponent<HeroSectionProps> = (
    props,
) => {
    const { shortPlaceName, location_slug } = useLocationContext();
    const things_to_do = gettext('THINGS TO DO');
    const { geo, placeDescription } = getGeoAndPlaceDescription(
        shortPlaceName,
        location_slug,
    );
    return (
        <header className="hero-section-header">
            <div
                className={`hero-section-header__main eds-g-cell-mn-7-12 ${
                    props.placeImage ? '' : 'hero-section-header__no-image'
                }`}
            >
                {props.placeImage ? (
                    <div className="hero-section-header__aside--small eds-g-cell eds-g-cell-1-1">
                        <PlaceImage
                            shortPlaceName={shortPlaceName}
                            placeImage={props.placeImage}
                            height={477}
                            width={792}
                        />
                        {props.isPageArt ? (
                            <PageArt
                                className="page-art-container--small"
                                artistInstagramLink={props.artistInstagramLink}
                                artistName={props.artistName}
                            />
                        ) : null}
                    </div>
                ) : (
                    <div className="circle-placeholder"></div>
                )}

                <div className="hero-section-header__main__content">
                    <div className="eds-g-cell-1-1 eds-g-cell--has-overflow eds-l-pad-hor-4">
                        <div className="hero-section-header__container">
                            <h1 className="hero-section-header__title">
                                {things_to_do}{' '}
                                <span className="hero-section-header__subtitle">
                                    {geo}
                                </span>
                            </h1>
                        </div>
                        <div className="eds-text-bm eds-l-mar-vert-4 spaced-paragraphs place-description">
                            {placeDescription}
                        </div>
                    </div>
                </div>
            </div>
            {props.placeImage ? (
                <div className="hero-section-header__aside--large">
                    <PlaceImage
                        shortPlaceName={shortPlaceName}
                        placeImage={props.placeImage}
                        height={477}
                        width={742}
                    />
                    {props.isPageArt ? (
                        <PageArt
                            className="page-art-container--large"
                            artistInstagramLink={props.artistInstagramLink}
                            artistName={props.artistName}
                        />
                    ) : null}
                </div>
            ) : null}
        </header>
    );
};
